<script setup lang="ts">
import { SortMode } from "@lxc/app-device-common";
import type LxcTable from "@lxc/app-device-common/src/components/LxcTable.vue";
import { watch } from "vue";
import type { LocationQuery } from "vue-router";
import TagForList from "~/core/components/TagForList.vue";
import { SearchMode } from "~/core/composables/useSearch";
import {
  DEFAULT_FIRST_PAGE,
  DEFAULT_PAGE_SIZE,
} from "~/core/constants/constants";
import type LxcError from "~/core/models/LxcError.class";
import { formatIsoDate } from "~/core/utils/date";
import { useCertificatesACLRoles } from "~/modules/certificate/composables/useCertificates";
import { useTruststore } from "~/modules/certificate/composables/useTruststore";
import type { TruststoreCertificate } from "~/modules/certificate/models/TruststoreCertificate.interface";
import { Filters } from "~/types";

const props = defineProps<{
  isTagsLoading: boolean;
  tags?: string[] | null;
  tagsError?: LxcError | null;
  pathContext: string;
}>();

const { t } = useI18n();
const route = useRoute();
const router = useRouter();
const {
  isLoading,
  results,
  filters,
  error,
  fetchData,
  setFilter,
  onSortChange,
  onSearch,
  search,
} = useTruststore(SearchMode.URL_SEARCH, true);

const { canManageCertificates, canViewCertificates } =
  useCertificatesACLRoles();

const searchQuery = computed({
  get() {
    return (filters.get(Filters.TRUSTSTORE_ALIAS) ?? "") as string;
  },
  set(searchQuery) {
    setFilter(Filters.TRUSTSTORE_ALIAS, searchQuery);
  },
});

const alias = ref<string | undefined | null>();
const tableRef = ref<InstanceType<typeof LxcTable>>();
const canEditTruststoreCertificate: ComputedRef<boolean> = computed(
  (): boolean => canManageCertificates() && !props.isTagsLoading,
);

/**
 * Update the selected certificate or redirect to certificate details page
 */
async function onSelectCertificate(certificate: TruststoreCertificate) {
  if (certificate?.alias && canViewCertificates()) {
    const targetUrl: Record<string, string | string[] | LocationQuery> = {
      path: `${props.pathContext}/${certificate?.alias}`,
      query: route.query,
    };
    await router.push(targetUrl);
  }
}

const onCloseCertificateDetail = () => {
  router.push({
    path: props.pathContext,
    query: route.query,
  });
};

const initDisplayFromRoute = () => {
  if (route.path.startsWith(props.pathContext)) {
    alias.value = route?.params?.uuid as string | undefined;
  }
};

/**
 * Retrieve certificates and set selected certificate if needed
 * @param page
 * @param pageSize
 */
async function loadData(
  page: number = DEFAULT_FIRST_PAGE,
  pageSize: number = DEFAULT_PAGE_SIZE,
) {
  await fetchData(page, pageSize);
}

function reloadWithContext() {
  loadData(
    results.value?.pagination?.page,
    results.value?.pagination?.pageSize,
  );
}

watch(route, initDisplayFromRoute);

onSearch(async () => {
  await loadData();
  initDisplayFromRoute();
});

defineExpose({
  search,
});
</script>

<template>
  <search-bar
    v-model:search-query="searchQuery"
    :search-placeholder="t('certificates.keystore.filters.searchByAlias')"
    class="grow shrink basis-0"
    @clear="search"
    @search="search"
  />
  <certificates-filters
    :filters="filters"
    :tags="tags"
    :is-tags-loading="isTagsLoading"
    :tag-filter-name="Filters.TRUSTSTORE_TAGS"
    :tags-error="tagsError"
    @change="setFilter"
    @enter="search"
  />
  <lxc-table
    ref="tableRef"
    min-width="63rem"
    :data="results?.results"
    :context="results?.pagination"
    :is-loading="isLoading"
    :error="error?.toError()"
    clickable
    :empty-text="t('certificates.noCertificate')"
    @change-page-and-page-size="loadData"
    @row-click="onSelectCertificate"
    @sort-change="onSortChange"
  >
    <lxc-table-column
      prop="alias"
      :label="t('certificates.alias')"
      :sort-mode="SortMode.CUSTOM"
      min-width="15rem"
    />
    <lxc-table-column
      prop="tags"
      :label="t('certificates.tag')"
      class="!py-0"
      width="20rem"
    >
      <template #default="scope">
        <tag-for-list :tags="scope.row.tags" :max="3" />
      </template>
    </lxc-table-column>
    <lxc-table-column
      prop="expirationDate"
      :label="t('certificates.expiration.timestamp.label')"
      width="16rem"
    >
      <template #default="scope">
        {{ formatIsoDate(scope.row.expirationDate, t("dateFormat.datetime")) }}
      </template>
    </lxc-table-column>
    <lxc-table-column class="!py-0" width="12rem">
      <template #default="scope">
        <div class="flex gap-2">
          <truststore-list-download-action :certificate="scope.row" />
          <truststore-list-delete-action
            :certificate="scope.row"
            @change="reloadWithContext"
          />
        </div>
      </template>
    </lxc-table-column>
  </lxc-table>

  <truststore-details
    :alias="alias"
    :disabled="!canEditTruststoreCertificate"
    :is-tags-loading="isTagsLoading"
    :tags-error="tagsError"
    :tags="tags"
    @close="onCloseCertificateDetail"
    @save="loadData"
  />
</template>

<style lang="scss" scoped>
:deep(table) {
  tbody {
    tr {
      &:hover {
        button {
          visibility: visible;
        }
      }
    }
  }
}
</style>
